import { useQueries } from "@tanstack/react-query";
import { differenceInDays, parseISO } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useCan } from "components/Can";
import { useTypedQuery } from "hooks/useTypedQuery";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { REPORT_GROUP, REPORT_IDS } from "modules/DealerReports";
import { DealerReportQueryKeys } from "modules/DealerReports/queryKeys";
import { parseDealerReportData } from "modules/DealerReports/util/parser";
import { ApiInstance } from "utils/Api";
import { translateReportForCharts } from "utils/common";

import type { QueryFunctionContext } from "@tanstack/react-query";
import type { QualityCheckFilters } from "modules/DealerReports/queryKeys";
import type { ParsedDealerReport } from "modules/DealerReports/util/parser";
import type { IBackendQueryKey } from "utils/keyFactory";

const filterReports = (reportIds: number[]) =>
  reportIds.filter(id => id !== REPORT_IDS.v7LocationOverview && id !== REPORT_IDS.v7LocationOverviewCommercial && id !== REPORT_IDS.v7LeaseOverview);

type UseDealerReportsParams = {
  dealer_id: number;
  date_from?: string;
  date_to?: string;
  custom_domain_enabled?: boolean;
  is_internal?: boolean | null;
  is_lease_company?: boolean | null;
  qualityCheckFilters?: QualityCheckFilters;
};

export const useDealerReports = ({
  dealer_id,
  date_from,
  date_to,
  custom_domain_enabled,
  is_internal,
  is_lease_company,
  qualityCheckFilters
}: UseDealerReportsParams) => {
  const { t } = useTranslation();

  const canListDealersReportEmail = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.DealersReportEmail);
  const canListDealersReportGroup = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.DealersReportGroup);

  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [currentReportIndex, setCurrentReportIndex] = useState<number>(-1);

  const initialGroupSetRef = useRef(false);
  const lastValidDatesRef = useRef<{ date_from: string; date_to: string } | null>(null);

  if (date_from && date_to) lastValidDatesRef.current = { date_from, date_to };
  const datesForQuery = lastValidDatesRef.current;

  const reportList = useTypedQuery({
    queryKey: DealerReportQueryKeys.listReports(dealer_id, Number(selectedGroup)),
    queryFn: async ({ endpoint, baseUrl, params }) => (await ApiInstance.post(endpoint, { ...params }, baseUrl)).data.template_ids ?? [],
    enabled: Boolean(selectedGroup && dealer_id),
    select: filterReports,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const constructRequestParams = (params?: Record<string, unknown>) => {
    if (params?.report_id !== REPORT_IDS.QualityCheck || !qualityCheckFilters) return params;

    return {
      ...params,
      quality_check_report_filter: {
        status: qualityCheckFilters.qcStatus,
        mechanic_id: qualityCheckFilters.mechanicId,
        qc_officer_id: qualityCheckFilters.qcOfficerId,
        dealer_location_id: qualityCheckFilters.locationId,
        quality_checklist_id: qualityCheckFilters.qcChecklistId
      }
    };
  };

  const getDealerReport = async ({ signal, queryKey }: QueryFunctionContext<ReadonlyArray<IBackendQueryKey>>, index: number) => {
    const { baseUrl, params } = queryKey[0];

    const response = await ApiInstance.post("/dealers/view", constructRequestParams(params), baseUrl, { signal });
    const report = response.data;
    const parsedReportData = report.report_data ? report.report_data.map((d: string) => JSON.parse(d)) : [];
    const parsedReportThreshold = report.thresholds ? JSON.parse(report.thresholds) : [];

    const processedData = report.report_type === "SimpleBarChart" ? parsedReportData.map((d: string[]) => translateReportForCharts(d, t)) : parsedReportData;

    const parsedDealerReportData = parseDealerReportData({ ...report, report_data: processedData });

    if (reportList.data && index < reportList.data.length - 1) setCurrentReportIndex(index);

    return {
      ...response.data,
      ...parsedDealerReportData,
      thresholds: parsedReportThreshold
    } as ParsedDealerReport;
  };

  const processEmailStats = (emailStatsData: Record<string, any>[]): Record<string, number>[] => {
    if (!emailStatsData.length) return [];

    const totals = emailStatsData.reduce(
      (acc, item) => ({
        accepted: acc.accepted + item.accepted.total,
        delivered: acc.delivered + item.delivered.total,
        failed: acc.failed + item.failed.permanent.total,
        stored: acc.stored + item.stored.total,
        complained: acc.complained + item.complained.total
      }),
      {
        accepted: 0,
        delivered: 0,
        failed: 0,
        stored: 0,
        complained: 0
      }
    );

    return [
      { [t("v8_accepted", "Accepted")]: totals.accepted },
      { [t("v8_delivered", "Delivered")]: totals.delivered },
      { [t("v8_failed", "Failed")]: totals.failed },
      { [t("v8_stored", "Stored")]: totals.stored },
      { [t("v8_complained", "Complained")]: totals.complained }
    ];
  };

  const groups = useTypedQuery({
    queryKey: DealerReportQueryKeys.listGroups,
    queryFn: async ({ endpoint, baseUrl }) => {
      const response = await ApiInstance.post(endpoint, { dealer_id }, baseUrl);
      setSelectedGroup(response.data.groups[0]);
      return response.data.groups;
    },
    enabled: canListDealersReportGroup && Boolean(dealer_id),
    staleTime: Infinity
  });

  useEffect(() => {
    if (groups.data && groups.data.length > 0 && !selectedGroup && !initialGroupSetRef.current) {
      initialGroupSetRef.current = true;
      setSelectedGroup(groups.data[0]);
    }
  }, [groups.data, selectedGroup]);

  useEffect(() => {
    setCurrentReportIndex(-1);
  }, [datesForQuery?.date_from, datesForQuery?.date_to, selectedGroup]);

  const reports = useQueries({
    queries:
      reportList.data?.map((report_id: number, index: number) => ({
        queryKey: DealerReportQueryKeys.dealerReport({
          index,
          dealer_id,
          report_id,
          date_from: datesForQuery?.date_from,
          date_to: datesForQuery?.date_to,
          is_internal,
          is_lease_company
        }),
        queryFn: (queryFnParams: QueryFunctionContext<ReadonlyArray<IBackendQueryKey>>) => getDealerReport(queryFnParams, index),
        enabled: !!report_id && !!datesForQuery && (index === 0 || index === currentReportIndex + 1),
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
      })) ?? []
  });

  const qcReport = reports.find(r => r.data?.id === REPORT_IDS.QualityCheck);

  useEffect(() => {
    qcReport?.refetch();
  }, [
    qualityCheckFilters?.mechanicId,
    qualityCheckFilters?.locationId,
    qualityCheckFilters?.qcOfficerId,
    qualityCheckFilters?.qcStatus,
    qualityCheckFilters?.qcChecklistId
  ]);

  const period = datesForQuery ? differenceInDays(parseISO(datesForQuery.date_to), parseISO(datesForQuery.date_from)).toString() : null;
  const emailStatsEnabled = selectedGroup === REPORT_GROUP.Communication && !!dealer_id && !!period && reports.every(r => r.isSuccess) && Boolean(custom_domain_enabled);

  const emailStats = useTypedQuery({
    queryKey: DealerReportQueryKeys.emailStats(dealer_id, datesForQuery?.date_from, datesForQuery?.date_to),
    queryFn: async ({ baseUrl, endpoint, params }) => processEmailStats((await ApiInstance.post(endpoint, { ...params }, baseUrl))?.data?.emails),
    enabled: emailStatsEnabled && canListDealersReportEmail,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  useEffect(() => {
    if (groups.isError) toast.error(t("v8_failed_to_load_report_groups", "Failed to load report groups"));
    if (reportList.isError) toast.error(t("v8_failed_to_load_report_list", "Failed to load report list"));
    if (reports.some(r => r.isError)) toast.error(t("v8_failed_to_load_report", "Failed to load report"));
  }, [groups.isError, reportList.isError, reports]);

  return {
    groups,
    selectedGroup,
    setSelectedGroup,
    reportList,
    reports,
    emailStats,
    loading: groups.isLoading || reportList.isLoading,
    error: groups.isError || reportList.isError
  };
};
